import React, { useState } from 'react';
import { Button } from '../atoms/Button';
import { exportWaltioCurrentSession, exportWaltioCurrentYear } from '../../utils/waltio';
import { exportPayoutsToCSV } from '../../utils/csv';
import { getCurrentDeclarationPeriod, filterPayoutsForDeclaration, calculateDeclarationTotal } from '../../utils/declaration';
import type { Payout } from '../../types/payout';
import type { DeclarationFrequency } from '../../types/settings';

interface WaltioExportProps {
  payouts: Payout[];
  declarationFrequency: DeclarationFrequency;
}

export const WaltioExport: React.FC<WaltioExportProps> = ({ payouts, declarationFrequency }) => {
  const [isExportingSession, setIsExportingSession] = useState(false);
  const [isExportingYear, setIsExportingYear] = useState(false);

  // Check if there are any "achat" type payouts
  const hasAchatPayouts = payouts.some(p => p.type === 'achat');
  const achatCount = payouts.filter(p => p.type === 'achat').length;

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  // Calculer les statistiques pour l&apos;affichage
  const sessionPayouts = payouts.filter(payout => {
    const payoutDate = new Date(payout.date);
    return payoutDate.getFullYear() === currentYear && 
           payoutDate.getMonth() === currentMonth;
  });

  const yearPayouts = payouts.filter(payout => {
    const payoutDate = new Date(payout.date);
    return payoutDate.getFullYear() === currentYear;
  });

  // Données pour la déclaration BNC
  const declarationPeriod = getCurrentDeclarationPeriod(declarationFrequency);
  const declarationPayouts = filterPayoutsForDeclaration(payouts, declarationFrequency);
  const declarationTotal = calculateDeclarationTotal(payouts, declarationFrequency);

  const sessionTotal = sessionPayouts.reduce((sum, payout) => sum + (payout.totalValue || 0), 0);
  const yearTotal = yearPayouts.reduce((sum, payout) => sum + (payout.totalValue || 0), 0);
  const totalValue = payouts.reduce((sum, payout) => sum + (payout.totalValue || 0), 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const monthName = new Date(currentYear, currentMonth).toLocaleDateString('fr-FR', { 
    month: 'long',
    year: 'numeric'
  });

  const handleExportSession = async () => {
    setIsExportingSession(true);
    try {
      await exportWaltioCurrentSession(payouts);
    } finally {
      setIsExportingSession(false);
    }
  };

  const handleExportYear = async () => {
    setIsExportingYear(true);
    try {
      await exportWaltioCurrentYear(payouts);
    } finally {
      setIsExportingYear(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center">
        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mr-4">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Exports de données
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Waltio, Déclaration BNC et Historique complet
          </p>
        </div>
      </div>

      {/* Contenu */}
      <div>
        {payouts.length === 0 ? (
          <div className="p-6">
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Aucun payout disponible
              </h4>
              <p className="text-gray-500 dark:text-gray-400">
                Ajoutez des payouts pour pouvoir exporter vos données
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Warning Banner for Achat transactions */}
            {hasAchatPayouts && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                      Attention : Transactions d&apos;achat détectées
                    </h4>
                    <p className="text-sm text-yellow-700">
                      Vous avez {achatCount} transaction{achatCount > 1 ? 's' : ''} de type &quot;achat&quot; dans vos données.
                      Ces transactions ne sont pas encore prises en charge par l&apos;export Waltio.
                      Seules les transactions de type &quot;payout&quot; seront exportées.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Section Export Waltio */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-4">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-purple-900">Export Waltio</h4>
                  <p className="text-sm text-purple-600">Format CSV compatible Waltio</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-purple-200 p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Session en cours</h5>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{monthName}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-purple-600">{formatCurrency(sessionTotal)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{sessionPayouts.length} payout{sessionPayouts.length > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <Button
                    onClick={handleExportSession}
                    disabled={sessionPayouts.length === 0 || isExportingSession}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExportingSession ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        <span>Export...</span>
                      </div>
                    ) : (
                      'Exporter la session'
                    )}
                  </Button>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-purple-200 p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Année en cours</h5>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{currentYear}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-purple-600">{formatCurrency(yearTotal)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{yearPayouts.length} payout{yearPayouts.length > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <Button
                    onClick={handleExportYear}
                    disabled={yearPayouts.length === 0 || isExportingYear}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExportingYear ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        <span>Export...</span>
                      </div>
                    ) : (
                      "Exporter l&apos;année"
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Section Déclaration BNC */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-lg border border-blue-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-blue-900">Déclaration BNC</h4>
                    <p className="text-sm text-blue-600">{declarationPeriod.label}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-900">{formatCurrency(declarationTotal)}</div>
                  <div className="text-xs text-blue-600">{declarationPayouts.length} payout{declarationPayouts.length > 1 ? 's' : ''}</div>
                </div>
              </div>
              <Button
                onClick={() => exportPayoutsToCSV(declarationPayouts, `declaration_${declarationFrequency}_${new Date().toISOString().split('T')[0]}.csv`)}
                disabled={declarationPayouts.length === 0}
                className="flex gap -2 items-center bg-blue-600 hover:bg-blue-700 text-white"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exporter déclaration BNC
              </Button>
            </div>

            {/* Section Historique complet */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-100 rounded-lg border border-emerald-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-emerald-900">Historique complet</h4>
                    <p className="text-sm text-emerald-600">Tous les payouts enregistrés</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-emerald-900">{formatCurrency(totalValue)}</div>
                  <div className="text-xs text-emerald-600">{payouts.length} payout{payouts.length > 1 ? 's' : ''}</div>
                </div>
              </div>
              <Button
                onClick={() => exportPayoutsToCSV(payouts)}
                className="flex gap -2 items-center bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exporter tout (CSV)
              </Button>
            </div>

            {/* Note d&apos;information */}
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p className="font-medium mb-1">Formats d&apos;export</p>
                  <p className="text-xs">
                    • <strong>Waltio :</strong> Format CSV spécialement adapté pour l&apos;import dans Waltio<br/>
                    • <strong>Déclaration BNC :</strong> Export des payouts de la période de déclaration en cours<br/>
                    • <strong>Historique :</strong> Export complet de tous les payouts au format CSV standard
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};